import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Loader2, CheckSquare, Square, Trash2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const CourseDeletionModal = ({ isOpen, onClose, courseId, onSuccess }) => {
    const [loading, setLoading] = useState(false); // Fetching linked items
    const [isDeleting, setIsDeleting] = useState(false);
    const [linkedItems, setLinkedItems] = useState({ tasks: [], notices: [], surveys: [] });
    const [selectedItems, setSelectedItems] = useState({ tasks: [], notices: [], surveys: [] });
    const [courseName, setCourseName] = useState('');

    useEffect(() => {
        if (isOpen && courseId) {
            fetchLinkedItems();
        } else {
            // Reset state on close
            setLinkedItems({ tasks: [], notices: [], surveys: [] });
            setSelectedItems({ tasks: [], notices: [], surveys: [] });
            setCourseName('');
        }
    }, [isOpen, courseId]);

    const fetchLinkedItems = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await api.get(`/courses/${courseId}/linked-items`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setCourseName(response.data.courseName);
                const { tasks, notices, surveys } = response.data;
                setLinkedItems({ tasks, notices, surveys });

                // Select all by default
                setSelectedItems({
                    tasks: tasks.map(t => t.id),
                    notices: notices.map(n => n.id),
                    surveys: surveys.map(s => s.id)
                });
            }
        } catch (error) {
            console.error('Error fetching linked items:', error);
            toast.error('Failed to load linked items');
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const toggleItemSelection = (type, id) => {
        setSelectedItems(prev => {
            const currentList = prev[type];
            const isSelected = currentList.includes(id);

            if (isSelected) {
                return { ...prev, [type]: currentList.filter(itemId => itemId !== id) };
            } else {
                return { ...prev, [type]: [...currentList, id] };
            }
        });
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            const token = localStorage.getItem('token');
            await api.post(`/courses/${courseId}/delete-advanced`, {
                itemsToDelete: selectedItems
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success('Course deleted successfully');
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error deleting course:', error);
            toast.error('Failed to delete course');
        } finally {
            setIsDeleting(false);
        }
    };

    if (!isOpen) return null;

    const totalItems = linkedItems.tasks.length + linkedItems.notices.length + linkedItems.surveys.length;
    const selectedCount = selectedItems.tasks.length + selectedItems.notices.length + selectedItems.surveys.length;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-red-600">
                        <AlertTriangle className="w-5 h-5" />
                        <h2 className="text-lg font-semibold">Delete Course</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                            <Loader2 className="w-8 h-8 animate-spin mb-2" />
                            <p>Loading linked items...</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-sm text-red-800">
                                <p className="font-medium mb-1">Warning: This action cannot be undone.</p>
                                <p>You are about to delete <strong>{courseName || 'this course'}</strong>.</p>
                            </div>

                            {totalItems > 0 ? (
                                <div className="space-y-4">
                                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                        Linked Items
                                        <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                            {selectedCount} selected for deletion
                                        </span>
                                    </h3>

                                    <p className="text-sm text-gray-600">
                                        Select the items you want to DELETE along with the course. Uncheck items to KEEP them (they will be removed from the course but remain in your library).
                                    </p>

                                    {/* Tasks */}
                                    {linkedItems.tasks.length > 0 && (
                                        <div className="space-y-2">
                                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Tasks</div>
                                            {linkedItems.tasks.map(task => (
                                                <label key={task.id} className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
                                                    <div className="mt-0.5">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedItems.tasks.includes(task.id)}
                                                            onChange={() => toggleItemSelection('tasks', task.id)}
                                                            className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">{task.title}</div>
                                                        <div className="text-xs text-gray-500 line-clamp-1">{task.description || 'No description'}</div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    )}

                                    {/* Notices */}
                                    {linkedItems.notices.length > 0 && (
                                        <div className="space-y-2">
                                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Notices</div>
                                            {linkedItems.notices.map(notice => (
                                                <label key={notice.id} className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
                                                    <div className="mt-0.5">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedItems.notices.includes(notice.id)}
                                                            onChange={() => toggleItemSelection('notices', notice.id)}
                                                            className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">{notice.title}</div>
                                                        <div className="text-xs text-gray-500 line-clamp-1">{notice.content}</div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    )}

                                    {/* Surveys */}
                                    {linkedItems.surveys.length > 0 && (
                                        <div className="space-y-2">
                                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Surveys</div>
                                            {linkedItems.surveys.map(survey => (
                                                <label key={survey.id} className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
                                                    <div className="mt-0.5">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedItems.surveys.includes(survey.id)}
                                                            onChange={() => toggleItemSelection('surveys', survey.id)}
                                                            className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">{survey.title}</div>
                                                        <div className="text-xs text-gray-500 line-clamp-1">{survey.description || 'No description'}</div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-600 italic">No linked items found for this course. Only the course structure will be deleted.</p>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50 rounded-b-xl">
                    <button
                        onClick={onClose}
                        disabled={isDeleting}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleDelete}
                        disabled={loading || isDeleting}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50"
                    >
                        {isDeleting ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Deleting...
                            </>
                        ) : (
                            <>
                                <Trash2 className="w-4 h-4" />
                                Delete {selectedCount > 0 ? `Course & ${selectedCount} Items` : 'Course'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CourseDeletionModal;
